/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

// Component imports
import { Avatar, Button, Card, Col, Input, Popover, Row, Typography, notification } from 'antd';
import { CopyOutlined, ShareAltOutlined } from '@ant-design/icons';
import DocumentTitle from '../components/DocumentTitle';
import { BotIcon } from '../components/Icons/BotIcon';

// Misc imports
import { API_WEBCHAT_URL } from '../constants/env';
import { useFetchPublicBotDeploymentQuery } from '../store/botDeployments/botDeploymentsApi';
import { copyTextToClipboard } from '../utils/function/copyToClipboard';
import { HELVIA_WEBSITE_URL } from '../constants/main';
import generateWebchatScript from '../utils/misc/generateWebchatScript';
import { useExtraLargeScreen } from '../utils/hooks/useExtraLargeScreen';
import AppLogo from '../assets/logo-full.svg';

const BotPreviewScreen = () => {
  const shareableLinkRef = useRef();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { currentUser } = useSelector((state) => state.auth);
  const { data: publicDeployment } = useFetchPublicBotDeploymentQuery({ deploymentId: searchParams.get('deploymentId') }, { skip: !searchParams.get('deploymentId') });

  const isXL = useExtraLargeScreen();

  const PREVIEW_PAGE_URL = window.location.href;
  const botDivId = 'botDiv';

  const notificationDump = useCallback((ref) => {
    notification.config({
      getContainer: () => ref
    });
  }, []);

  useEffect(()=>{
    document.querySelector('.ant-layout').style.setProperty('background', `url(https://res.cloudinary.com/helvia-io/image/upload/o_35/v1632235838/Helvia.io/AdobeStock_332962588.jpg)`, 'important');
    document.querySelector('.ant-layout').style.setProperty('background-size', 'cover', 'important');
    document.querySelector('.ant-layout').style.setProperty('background-blend-mode', 'hard-light', 'important');
    document.querySelector('.ant-layout').style.setProperty('background-repeat', 'no-repeat', 'important');
    document.querySelector('.ant-layout-content').style.setProperty('padding', '0 0 20px', 'important');
  }, []);

  useEffect(() => {
    if (!publicDeployment) {
      return;
    }
    const mainColor = publicDeployment.settings.layout?.microsoftWebChat?.configuration?.mainColor ?? 'blue';
    document.querySelector('.ant-layout').style.setProperty('background-color', mainColor, 'important');
  }, [publicDeployment]);

  const loadWebchatScript = () => {
    let mode = searchParams.get('mode');
    if (!document.getElementById('chat-script')) {
      const scriptToAdd = generateWebchatScript({
        deploymentId: searchParams.get('deploymentId'),
        introNode: searchParams.get('greeting'),
        isAnalyticsEnabled: searchParams.get('analytics') === 'true',
        metadata: searchParams.get('metadata'),
        userName: currentUser?.fullName,
        userEmail: currentUser?.email,
        userId: currentUser?.id,
        mode: mode,
        ...(searchParams.get('language') && { language: searchParams.get('language') })
      });

      const script = document.createElement('script');
      script.id = 'chat-script';
      const scriptFunc = document.createTextNode(scriptToAdd);
      script.appendChild(scriptFunc);
      const parent = document.getElementById(botDivId).parentElement;
      parent.appendChild(script);
    }
  };

  const botDivRef = useCallback(node => {
    if (node !== null) {
      const script = document.createElement('script');
      script.src = API_WEBCHAT_URL;
      document.body.appendChild(script);
      script.onload = () =>{
        loadWebchatScript();
      };
    }
  }, []);

  return (
    <div className="bot-preview-page">
      <DocumentTitle title={t('Preview')} />

      {
        publicDeployment ? (
          <Fragment>
            <Row
              align="middle"
              justify="center"
              className="mb-5 py-5"
              style={{ backgroundColor: publicDeployment.settings.layout?.microsoftWebChat?.configuration?.mainColor ?? 'blue' }}
            >
              <Col xs={18}>
                <a href={HELVIA_WEBSITE_URL}>
                  <img
                    src={AppLogo}
                    alt="logo"
                    width={160}
                    className="shadow bg-white py-3 px-6"
                    style={{ borderRadius: 50 }}
                  />
                </a>
              </Col>
            </Row>

            <Row justify="center">
              <Col xs={22} lg={18}>
                <Row
                  gutter={36}
                  wrap={!isXL}
                >
                  <Col
                    className={classNames('bot-preview-col', {
                      'p-0': (searchParams.get('mode') || publicDeployment.settings.layout?.microsoftWebChat?.configuration?.mode) === 'BUBBLE'
                    })}
                    flex={
                      (searchParams.get('mode') || publicDeployment.settings.layout?.microsoftWebChat?.configuration?.mode) === 'BUBBLE'
                        ? 'none'
                        : isXL ? '0 1 67%' : '1 1 100%'
                    }
                  >
                    <div id={botDivId} ref={botDivRef}/>
                  </Col>

                  <Col
                    flex={isXL ? '1 0 33%' : '1 1 100%'}
                    style={{ maxWidth: 480 }}
                  >
                    <Card bordered={false} className="mt-10">
                      <Row
                        className="mb-5"
                        wrap={false}
                        justify="space-between"
                        align="middle"
                      >
                        <Avatar
                          src={publicDeployment.tenant?.image}
                          size={68}
                        >
                          <BotIcon className="fs-xxl mt-5" />
                        </Avatar>

                        <Popover
                          title={<span>{t('Copy link to clipboard')}</span>}
                          placement="topRight"
                          content={
                            <span className="d-flex">
                              <Input
                                ref={shareableLinkRef}
                                className="mb-0 mr-1 align-self-center"
                                value={PREVIEW_PAGE_URL}
                                style={{ borderRadius: 4 }}
                                readOnly={true}
                              />

                              <Button
                                key="copy"
                                type="link"
                                icon={<CopyOutlined/>}
                                className="border-radius-base border-none"
                                style={{
                                  color: publicDeployment.settings.layout?.microsoftWebChat?.configuration?.mainFontColor ?? 'white',
                                  backgroundColor: publicDeployment.settings.layout?.microsoftWebChat?.configuration?.mainColor ?? 'blue'
                                }}
                                onClick={() => {
                                  shareableLinkRef.current.select();
                                  copyTextToClipboard(PREVIEW_PAGE_URL, 'Preview link copied to clipboard');
                                }}
                              />
                            </span>
                          }
                          trigger="click"
                        >
                          <Button
                            className="shadow-none border-0"
                            key="share"
                            icon={<ShareAltOutlined/>}
                            style={{
                              borderRadius: 20,
                              color: publicDeployment.settings.layout?.microsoftWebChat?.configuration?.mainFontColor ?? 'white',
                              backgroundColor: publicDeployment.settings.layout?.microsoftWebChat?.configuration?.mainColor ?? 'blue'
                            }}
                            type="link"
                          >
                            {t('Share')}
                          </Button>
                        </Popover>
                      </Row>

                      <h2><strong>{searchParams.get('name') || t('(No name available)')}</strong></h2>
                      <h3>{searchParams.get('description') || t('(No description available)')}</h3>
                      <h3>
                        <Typography.Text strong>{t('Deployment: ')}</Typography.Text>
                        {publicDeployment?.displayName || <Typography.Text type="secondary">{t('(No display name available)')}</Typography.Text>}
                      </h3>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>

            <div
              ref={notificationDump}
              className="d-none"
            />
          </Fragment>
        ) : null
      }
    </div>
  );
};

export default BotPreviewScreen;
